"use client";

// UserChip — you, top-right, on every innernet surface. ONE chip, ONE size,
// ONE behaviour.
//
// Signed in on the dashboard, the docs, the blog, a skill page, the admin
// console, anywhere: your name and your face sit where the "enter" door sits
// for a stranger, so a signed-in person never has to wonder whether this page
// knows them. The mark is the dashboard's exact lockup — the name at the crumb
// size, the cell at 48 (38 on a phone, where the name also steps away) — and
// pressing it does one thing everywhere: it takes you home, to your innernet.
//
// It used to open a small "you" takeover (home · settings · sign out) on the
// public pages while the dashboard sent you to settings. Two behaviours for one
// mark, and the takeover's veil + netti choreography fought the page under it
// (the company page glitched on every press). Now the chip is a DOOR, full
// stop: settings and sign-out live inside the dashboard, one press away.
//
// It asks /api/me/card for itself rather than being handed a viewer, because it
// mounts on statically rendered pages too (law: the chrome must not force a
// page dynamic). A host that already knows who is looking (the dashboard, the
// admin console) hands the card in and skips the fetch. Signed out it renders
// only the `fallback`.

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import CellAvatar from "./CellAvatar";

export interface MeCard {
  signedIn: boolean;
  name: string;
  avatarUrl: string | null;
  founder: boolean;
}

/** the ONE size of the mark — the dashboard's, everywhere. */
export const CHIP_CELL = 48;
export const CHIP_CELL_NARROW = 38;
/** the phone breakpoint the dashboard shell uses for its chrome. */
export const CHIP_NARROW_QUERY = "(max-width: 640px)";

/** cached for the tab's life so moving between pages doesn't re-ask. */
let cached: MeCard | null = null;
let inflight: Promise<MeCard | null> | null = null;

export async function loadCard(): Promise<MeCard | null> {
  if (cached) return cached;
  if (!inflight) {
    inflight = fetch("/api/me/card", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: MeCard | null) => {
        cached = j && j.signedIn ? j : null;
        return cached;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** forget the cached card — sign-out calls this so the chip doesn't linger. */
export function forgetCard() { cached = null; }

/** true under the dashboard's phone breakpoint — the cell steps down to 38. */
export function useChipNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(CHIP_NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

export default function UserChip({
  fallback = null,
  card,
  preview,
  onPress,
  avatarRef,
  className,
  style,
}: {
  /** what stands in this slot for a stranger — usually the "enter" door. */
  fallback?: React.ReactNode;
  /** a host that already knows who is looking hands the card in — no fetch. */
  card?: MeCard | null;
  /** stand in for the fetch (the dev chrome lab). Never set in the product. */
  preview?: MeCard;
  /** the dashboard is already home: it steps the shell back to its root in
   *  place instead of navigating. Everywhere else the chip pushes /dashboard. */
  onPress?: () => void;
  /** the dashboard measures the cell's centre to seat netti in the you space. */
  avatarRef?: React.Ref<HTMLSpanElement>;
  className?: string;
  style?: React.CSSProperties;
}) {
  const given = card ?? preview ?? null;
  // the fetched card (only consulted when no host handed one in)
  const [fetched, setFetched] = useState<{ card: MeCard | null; done: boolean }>({ card: cached, done: cached !== null });
  const [busy, setBusy] = useState(false);
  const router = useRouter();
  const narrow = useChipNarrow();

  useEffect(() => {
    if (given) return;
    let live = true;
    loadCard().then((c) => { if (live) setFetched({ card: c, done: true }); });
    return () => { live = false; };
  }, [given]);

  const me = given ?? fetched.card;
  const resolved = !!given || fetched.done;

  // law_always_there — this slot never pops or flips. Until we know who is
  // looking it holds its space at zero opacity, then the right thing FADES in.
  // Painting the "enter" door first and swapping it for a face (or the reverse)
  // is the one thing worse than a beat of quiet: chrome that was briefly wrong.
  if (!me) {
    return (
      <span className={`user-slot${className ? ` ${className}` : ""}`} data-resolved={resolved || undefined} style={style}>
        {fallback}
      </span>
    );
  }

  const go = () => {
    if (onPress) { onPress(); return; }
    setBusy(true);
    // the dashboard host (when configured) is reached through the proxy's
    // app-path redirect — /dashboard is the one address that lands you home
    // from every origin.
    router.push("/dashboard");
  };

  return (
    <span className={`user-slot${className ? ` ${className}` : ""}`} data-resolved style={style}>
      <button
        type="button"
        className="user-chip"
        title="your innernet"
        aria-label={`you — ${me.name} — open your innernet`}
        data-noswipe
        data-busy={busy || undefined}
        onClick={go}
      >
        <span className="user-chip__name os-username">{me.name}</span>
        {/* the profile mark wears the innernet CELL silhouette (law_cell) — one
            shared CellAvatar, sized to the dashboard crumb everywhere. Wrapped
            so its centre can be measured — CellAvatar takes no ref. */}
        <span ref={avatarRef} style={{ display: "inline-flex", lineHeight: 0 }}>
          <CellAvatar
            name={me.name}
            src={me.avatarUrl}
            className="os-avatar"
            size={narrow ? CHIP_CELL_NARROW : CHIP_CELL}
            fontSize="var(--t-crumb)"
          />
        </span>
      </button>
    </span>
  );
}
